
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { User, AuthError } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";

interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  company?: string;
  recaptchaToken: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{
    success: boolean;
    message?: string;
  }>;
  signUp: (data: SignUpData) => Promise<{
    success: boolean;
    message?: string;
  }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getAuthErrorMessage = (error: AuthError): string => {
  switch (error.message) {
    case "Invalid login credentials":
      return "Email ou mot de passe incorrect";
    case "Email not confirmed":
      return "Veuillez confirmer votre email avant de vous connecter";
    case "User not found":
      return "Aucun compte trouvé avec cet email";
    case "Invalid email":
      return "Format d'email invalide";
    default:
      console.error("Unexpected auth error:", error);
      return "Une erreur est survenue lors de la connexion";
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const initSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
      } catch (error) {
        console.error("Error getting session:", error);
      } finally {
        setLoading(false);
      }
    };

    initSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      
      if (event === 'SIGNED_OUT') {
        setUser(null);
        navigate('/account');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, toast]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return {
          success: false, 
          message: getAuthErrorMessage(error)
        };
      }

      if (!data?.user) {
        return {
          success: false, 
          message: "Erreur de connexion" 
        };
      }

      navigate('/');
      
      return { success: true };
    } catch (error: any) {
      return {
        success: false, 
        message: "Une erreur est survenue" 
      };
    }
  };

  const signUp = async (signUpData: SignUpData) => {
    try {
      // Vérifier le reCAPTCHA avec l'edge function
      const { data: recaptchaResult, error: recaptchaError } = await supabase.functions.invoke('verify-recaptcha', {
        body: { token: signUpData.recaptchaToken }
      });

      if (recaptchaError || !recaptchaResult?.success) {
        return {
          success: false,
          message: "Échec de la vérification reCAPTCHA. Veuillez réessayer."
        };
      }

      const { error, data } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: {
          data: {
            first_name: signUpData.firstName,
            last_name: signUpData.lastName,
            phone: signUpData.phone,
            street_address: signUpData.streetAddress,
            city: signUpData.city,
            province: signUpData.province,
            postal_code: signUpData.postalCode,
            company: signUpData.company
          }
        }
      });

      if (error) {
        return {
          success: false,
          message: getAuthErrorMessage(error)
        };
      }
      
      toast({
        title: "Compte créé avec succès",
        description: "Veuillez vérifier votre email pour confirmer votre compte.",
      });

      return { success: true };
    } catch (error: any) {
      return {
        success: false, 
        message: error.message || "Une erreur est survenue lors de l'inscription" 
      };
    }
  };

  const signOut = async () => {
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        toast({
          title: "Erreur",
          description: "Une erreur est survenue lors de la déconnexion",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la déconnexion",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
